/**
 * PbPhaseStepper — Process-Stepper for PB phase 1–5.
 *
 * Displays a horizontal stepper with phase labels.
 * Completed steps have a filled circle, the active step is highlighted,
 * future steps are muted. An optional "Weiter"-Button advances to the
 * next phase (disabled at phase 5).
 *
 * feature/pb-phase-process
 * updated: PB Phasen management — phases can now be loaded from the DB
 */
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, BRAND_DARK, getThemeColors } from '../lib/theme';
import { Button } from './ui';
import type { PbPhase } from '../lib/v1';

/** Phase labels keyed by phase number (1–5). Must stay in sync with pb_phases.php. */
export const PB_PHASE_KEYS: Record<number, string> = {
  1: 'pflege.phase.label.1',
  2: 'pflege.phase.label.2',
  3: 'pflege.phase.label.3',
  4: 'pflege.phase.label.4',
  5: 'pflege.phase.label.5',
};

export const PB_PHASE_COUNT = 5;

interface PbPhaseStepperProps {
  /** Current phase id (1-based). Used as currentPhaseId internally. */
  phase: number;
  /** DB phases — when provided and non-empty, replaces hardcoded fallback. */
  phases?: PbPhase[];
  /** Whether to show the "Weiter" button. */
  showAdvance?: boolean;
  /** Called when the "Weiter" button is clicked. Should trigger PATCH and optimistically update phase. */
  onAdvance?: () => void;
  /** Whether the advance action is currently in-flight. */
  advancing?: boolean;
  dark?: boolean;
}

export function PbPhaseStepper({
  phase,
  phases,
  showAdvance = true,
  onAdvance,
  advancing = false,
  dark = false,
}: PbPhaseStepperProps) {
  const { t: tr } = useTranslation();
  const t = getThemeColors(dark);

  // Build the display list: DB phases if provided+non-empty, else fallback
  const displayPhases: Array<{ id: number; label: string; color: string }> =
    phases && phases.length > 0
      ? phases.map(p => ({ id: p.id, label: p.title, color: p.color }))
      : Array.from({ length: PB_PHASE_COUNT }, (_, i) => ({
          id: i + 1,
          label: tr(PB_PHASE_KEYS[i + 1] ?? `Phase ${i + 1}`),
          color: BRAND,
        }));

  const currentPhaseId = phase;
  const maxPhaseId = displayPhases.length > 0
    ? Math.max(...displayPhases.map(p => p.id))
    : PB_PHASE_COUNT;
  const atMax = currentPhaseId >= maxPhaseId;

  // Schmale Viewports (Handy): jeder Schritt hat eine Mindestbreite, damit die
  // Labels nicht ineinanderlaufen; reicht die Breite nicht, scrollt die Spur
  // horizontal und die aktive Phase wird in den sichtbaren Bereich geholt.
  const trackRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const track = trackRef.current;
    const step = activeRef.current;
    if (!track || !step || track.scrollWidth <= track.clientWidth) return;
    track.scrollLeft = Math.max(0, step.offsetLeft - (track.clientWidth - step.offsetWidth) / 2);
  }, [currentPhaseId, displayPhases.length]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
      {/* Stepper track */}
      <div
        ref={trackRef}
        data-testid="pb-phase-track"
        style={{ display: 'flex', alignItems: 'flex-start', gap: 0, position: 'relative', overflowX: 'auto', minWidth: 0 }}
      >
        {displayPhases.map((dbPhase, i) => {
          const pos     = i + 1; // 1-based position in display list
          const posOfCurrent = displayPhases.findIndex(p => p.id === currentPhaseId) + 1;
          const done    = pos < posOfCurrent;
          const active  = dbPhase.id === currentPhaseId;
          const stepColor = (dbPhase.color && dbPhase.color !== '') ? dbPhase.color : BRAND;

          return (
            <React.Fragment key={dbPhase.id}>
              {/* Step */}
              <div ref={active ? activeRef : undefined} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                flex: '1 0 64px', minWidth: 64, position: 'relative',
              }}>
                {/* Connector left — not for first step */}
                {pos > 1 && (
                  <div style={{
                    position: 'absolute',
                    top: 12,
                    right: '50%',
                    width: '100%',
                    height: 2,
                    background: done || active ? stepColor : t.border,
                    zIndex: 0,
                  }} />
                )}

                {/* Circle */}
                <div style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: done || active ? stepColor : t.cardMuted,
                  border: `2px solid ${done || active ? stepColor : t.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  position: 'relative', zIndex: 1,
                  transition: 'background 0.2s, border-color 0.2s',
                  flexShrink: 0,
                }}>
                  {done ? (
                    // Checkmark SVG
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <span style={{
                      fontSize: 11, fontWeight: 700, lineHeight: 1,
                      color: active ? '#fff' : t.muted,
                    }}>
                      {pos}
                    </span>
                  )}
                </div>

                {/* Label */}
                <div style={{
                  marginTop: 6, fontSize: 10, fontWeight: active ? 700 : 500,
                  color: active ? BRAND_DARK : done ? t.text : t.muted,
                  textAlign: 'center', lineHeight: 1.3, maxWidth: 72, padding: '0 2px',
                  overflowWrap: 'break-word', hyphens: 'auto',
                  transition: 'color 0.2s',
                }}>
                  {dbPhase.label}
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Advance button */}
      {showAdvance && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Button
            variant="primary"
            size="sm"
            dark={dark}
            disabled={atMax || advancing}
            onClick={onAdvance}
          >
            {advancing
              ? tr('pflege.phase.advancing')
              : atMax
                ? tr('pflege.phase.completed')
                : tr('pflege.phase.advance')}
          </Button>
          {atMax && (
            <span style={{ fontSize: 12, color: t.muted }}>
              {tr('pflege.phase.completed_hint')}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Compact phase badge for use in table rows.
 * Shows phase number + label in a colour-coded pill.
 */
export function PhaseBadge({
  phase,
  phaseLabel,
  phaseColor,
  dark = false,
}: {
  phase: number;
  phaseLabel: string | null;
  phaseColor?: string;
  dark?: boolean;
}) {
  const { t: tr } = useTranslation();

  // Colour ramp: 1=neutral → 2=info → 3=warning → 4=brand → 5=success
  type Tone = 'neutral' | 'info' | 'warning' | 'brand' | 'success';
  const TONE_BY_PHASE: Record<number, Tone> = {
    1: 'neutral',
    2: 'info',
    3: 'warning',
    4: 'brand',
    5: 'success',
  };
  const tones: Record<Tone, { bg: string; color: string }> = {
    neutral: { bg: dark ? '#1e2735' : '#f2f4f7', color: dark ? '#cfd7e3' : '#344054' },
    info:    { bg: dark ? '#0d1d2e' : '#ecf3ff', color: dark ? '#93b4ff' : '#2a31d8' },
    warning: { bg: dark ? '#2c2010' : '#fffaeb', color: dark ? '#fbc97a' : '#b54708' },
    brand:   { bg: dark ? '#2a1222' : '#fce7f4', color: dark ? '#f49dd1' : BRAND },
    success: { bg: dark ? '#0d2a1e' : '#ecfdf3', color: dark ? '#6ee7a8' : '#027a48' },
  };

  // If phaseColor is provided and non-empty, use it directly
  const st = (phaseColor && phaseColor !== '')
    ? { bg: phaseColor, color: '#fff' }
    : tones[TONE_BY_PHASE[phase] ?? 'neutral'];

  const label = phaseLabel ?? tr(PB_PHASE_KEYS[phase] ?? 'pflege.phase.unknown');

  return (
    <span style={{
      background: st.bg, color: st.color,
      borderRadius: 9999, padding: '2px 9px',
      fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap',
      display: 'inline-flex', alignItems: 'center', gap: 4,
    }}>
      <span style={{ opacity: 0.7, fontSize: 10 }}>{phase}</span>
      {label}
    </span>
  );
}
